import React, { useState } from 'react';
import { 
  Search, Filter, Plus, Edit, AlertTriangle, Package,
  TrendingUp, TrendingDown, RefreshCw, Download, Upload,
  CheckCircle, Clock, BarChart3
} from 'lucide-react';

const SellerInventory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Dummy inventory data
  const dummyInventory = [
    {
      id: '1',
      name: 'Premium Cotton T-Shirt',
      sku: 'CT-001',
      category: 'T-Shirts',
      currentStock: 150,
      availableStock: 125,
      reservedStock: 25,
      reorderLevel: 50,
      location: 'Zone A - Shelf 12',
      supplier: 'Cotton Mills Ltd.',
      unitCost: 15.00,
      sellingPrice: 29.99,
      status: 'in_stock',
      lastRestocked: '2024-02-28'
    },
    {
      id: '2',
      name: 'Cotton Polo Shirt',
      sku: 'PS-002',
      category: 'Polo Shirts',
      currentStock: 15,
      availableStock: 5,
      reservedStock: 10,
      reorderLevel: 30,
      location: 'Zone A - Shelf 15',
      supplier: 'Premium Textiles',
      unitCost: 18.50,
      sellingPrice: 34.99,
      status: 'critical',
      lastRestocked: '2024-02-20'
    },
    {
      id: '3',
      name: 'Casual Cotton Pants',
      sku: 'CP-003',
      category: 'Pants',
      currentStock: 89,
      availableStock: 74,
      reservedStock: 15,
      reorderLevel: 40,
      location: 'Zone B - Shelf 8',
      supplier: 'Fashion Forward Co.',
      unitCost: 22.00,
      sellingPrice: 39.99,
      status: 'in_stock',
      lastRestocked: '2024-02-25'
    },
    {
      id: '4',
      name: 'Premium Cotton Hoodie',
      sku: 'CH-004',
      category: 'Hoodies',
      currentStock: 0,
      availableStock: 0,
      reservedStock: 0,
      reorderLevel: 25,
      location: 'Zone B - Shelf 20',
      supplier: 'Comfort Wear Ltd.',
      unitCost: 28.00,
      sellingPrice: 49.99,
      status: 'out_of_stock',
      lastRestocked: '2024-01-15'
    },
    {
      id: '5',
      name: 'Cotton Casual Shirt',
      sku: 'CS-005',
      category: 'Shirts',
      currentStock: 45,
      availableStock: 40,
      reservedStock: 5,
      reorderLevel: 30,
      location: 'Zone A - Shelf 18',
      supplier: 'Classic Shirts Inc.',
      unitCost: 16.50,
      sellingPrice: 32.99,
      status: 'low_stock',
      lastRestocked: '2024-02-22'
    }
  ];

  // Note: Inventory would be fetched from database when inventory system is implemented
  React.useEffect(() => {
    // Placeholder for future inventory fetching
    setInventory(dummyInventory);
    setLoading(false);
  }, []);

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleSelectItem = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems.length === filteredInventory.length 
        ? [] 
        : filteredInventory.map(item => item.id)
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          In Stock
        </span>;
      case 'low_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Low Stock
        </span>;
      case 'critical':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Critical
        </span>;
      case 'out_of_stock':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Package className="h-3 w-3 mr-1" />
          Out of Stock
        </span>;
      default:
        return null;
    }
  };

  const getStockLevel = (current: number, reorder: number) => {
    if (current === 0) return 'text-red-600';
    if (current <= reorder) return 'text-orange-600';
    if (current <= reorder * 1.5) return 'text-yellow-600';
    return 'text-green-600';
  };

  const totalValue = inventory.reduce((sum, item) => sum + ((item.currentStock || 0) * (item.unitCost || 0)), 0);
  const lowStockItems = inventory.filter(item => (item.currentStock || 0) <= (item.reorderLevel || 0)).length;
  const outOfStockItems = inventory.filter(item => (item.currentStock || 0) === 0).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
              <p className="text-gray-600">Track stock levels and manage inventory</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Upload className="h-4 w-4" />
                <span>Import Stock</span>
              </button>
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export Report</span>
              </button>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Stock</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search inventory by name or SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="in_stock">In Stock</option>
                <option value="low_stock">Low Stock</option>
                <option value="critical">Critical</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{inventory.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">${totalValue.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold text-gray-900">{lowStockItems}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <Package className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                <p className="text-2xl font-bold text-gray-900">{outOfStockItems}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Inventory ({filteredInventory.length})</h2>
              {selectedItems.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedItems.length} selected</span>
                  <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">Bulk Update</button>
                </div>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : inventory.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Inventory Items</h3>
              <p className="text-gray-600 mb-6">Add products to start managing your inventory.</p>
              <Link 
                to="/seller-central/products/add"
                className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors inline-block"
              >
                Add Your First Product
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === filteredInventory.length && filteredInventory.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Levels</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pricing</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInventory.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                          <div className="text-xs text-gray-400">{item.category}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className={`text-sm font-medium ${getStockStatus(item.currentStock, item.reorderLevel)}`}>
                            Current: {item.currentStock}
                          </div>
                          <div className="text-xs text-gray-500">
                            Available: {item.availableStock}
                          </div>
                          <div className="text-xs text-gray-500">
                            Reserved: {item.reservedStock}
                          </div>
                          <div className="text-xs text-gray-400">
                            Reorder at: {item.reorderLevel}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-gray-900">
                            Sell: ${item.sellingPrice}
                          </div>
                          <div className="text-xs text-gray-500">
                            Cost: ${item.unitCost}
                          </div>
                          <div className="text-xs text-green-600">
                            Margin: {(((item.sellingPrice - item.unitCost) / item.sellingPrice) * 100).toFixed(1)}%
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(item.status)}
                        <div className="text-xs text-gray-500 mt-1">
                          Last restocked: {item.lastRestocked}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{item.location}</div>
                        <div className="text-xs text-gray-500">{item.supplier}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button className="text-gray-400 hover:text-gray-600" title="Edit Stock">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600" title="Restock">
                            <RefreshCw className="h-4 w-4" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600" title="View History">
                            <BarChart3 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerInventory;